import { signS3AgentSessionAttachment } from "@api/SignS3";
import {
  useApolloClient,
  useLazyQuery,
  useMutation,
} from "@apollo/client/react";
import { alert } from "@components/common/Alert";
import { UPLOAD_AGENT_SESSION_ATTACHMENT } from "@graphql/mutations/UploadAgentSessionAttachment";
import { GET_AGENT_SESSION_ATTACHMENTS } from "@graphql/queries/GetAgentSessionAttachments";
import { GET_AGENT_SESSION_EVENTS } from "@graphql/queries/GetAgentSessionEvents";
import { GET_AGENT_SESSIONS } from "@graphql/queries/GetAgentSessions";
import { useAuth, useWatchUserWsEvent, useWS } from "@hooks";
import { useCompanionTooltipsDisabled } from "@hooks/use-companion-tooltips-disabled";
import { isAnyTourActive } from "@hooks/use-tour";
import type {
  AnyWebSocketEvent,
  CompanionAttachmentEvent,
  CompanionEscalateSupportData,
  CompanionEscalateSupportEvent,
  CompanionHeartbeatEvent,
  CompanionMessageEvent,
  CompanionNavExecuteEvent,
  CompanionNavProposalEvent,
  CompanionToolPermissionDetail,
  CompanionToolPermissionRequestData,
  CompanionToolPermissionRequestEvent,
  CompanionToolPermissionResolvedEvent,
  CompanionToolUseEvent,
  CompanionTurnEndEvent,
  CompanionTurnErrorEvent,
} from "@src/codegen/ws-schema";
import { SCRIBENOTE_FLASK } from "@src/constants";
import axios from "axios";
import { useFlags } from "launchdarkly-react-client-sdk";
import mixpanel from "mixpanel-browser";
import moment from "moment";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useEffectEvent,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useLocation, useNavigate } from "react-router";

import {
  extractTrailingToolUses,
  mapEventsToMessages,
  normalizeCompanionList,
  normalizeCompanionType,
  shouldHideToolUse,
  type CompanionIdentity,
  type RawEvent,
  type ToolCall,
  type ToolCallState,
} from "./use-companion-v2.mapEvents";

export type { CompanionIdentity, ToolCall, ToolCallState };

export type CompanionEffort =
  | "standard"
  | "high"
  | "support"
  | "support_mph";

export interface CompanionTierFlags {
  showGlobalCompanionTierLow: boolean;
  showGlobalCompanionTierHigh: boolean;
}

export function isCompanionEffortEnabled(
  effort: CompanionEffort | null | undefined,
  flags: CompanionTierFlags,
  allowCompanionOptIn: boolean = true,
): boolean {
  if (effort === "support" || effort === "support_mph") return true;
  if (!allowCompanionOptIn) return false;
  switch (effort) {
    case "standard":
      return flags.showGlobalCompanionTierLow;
    case "high":
      return flags.showGlobalCompanionTierHigh;
    default:
      return true;
  }
}

export type NavKind = "note" | "patient" | "template" | "page";

export interface NavPayload {
  kind: NavKind;
  path: string;
  label: string;
  reason?: string;
  // Record destinations only
  entity_uuid?: string;
  // Static-page destinations only
  page?: string;
  tab?: string;
}

export type ToolPermissionStatus = NonNullable<
  CompanionToolPermissionRequestData["status"]
>;
export type ToolPermissionDecision = "approve" | "reject";

export { CompanionToolPermissionDetail };

export interface ToolPermissionPayload {
  requestId: string;
  agentSessionUuid: string;
  toolName: string;
  toolAction: string;
  toolKind: "builtin" | "custom";
  title: string;
  summary: string;
  details: CompanionToolPermissionDetail[];
  canPersist: boolean;
  persistLabel: string;
  grantDisplayName: string;
  expiresAt: string | null;
  status: ToolPermissionStatus;
  isResolving?: boolean;
  error?: string | null;
}

function createPermissionPayload(
  fields: Omit<ToolPermissionPayload, "isResolving" | "error">,
): ToolPermissionPayload {
  return { ...fields, error: null };
}

const AGENT_API = `${SCRIBENOTE_FLASK}/api/companion/v2`;

class HttpError extends Error {
  constructor(
    message: string,
    public status: number,
  ) {
    super(message);
  }
  get canRetry() {
    return this.status !== 402;
  }
  get isOutOfTreats() {
    return this.status === 402;
  }
}

type CompanionWsEvent =
  | CompanionMessageEvent
  | CompanionToolUseEvent
  | CompanionTurnEndEvent
  | CompanionTurnErrorEvent
  | CompanionHeartbeatEvent
  | CompanionAttachmentEvent
  | CompanionNavProposalEvent
  | CompanionNavExecuteEvent
  | CompanionToolPermissionRequestEvent
  | CompanionToolPermissionResolvedEvent
  | CompanionEscalateSupportEvent;

function isCompanionWsEvent(
  e: AnyWebSocketEvent,
): e is CompanionWsEvent {
  return e.event?.startsWith?.("companion-") ?? false;
}

// Codegen produces a generated alias (`Reason2` or similar — the name
// json-schema-to-typescript picks isn't stable). Re-export under a
// readable name so consumers don't depend on the codegen alias and the
// FE has zero hand-rolled duplicates of the BE enum.
export type EscalationReason = CompanionEscalateSupportData["reason"];

export interface EscalationRequest {
  lastUserMessage: string | null;
  lastAssistantMessage: string | null;
  agentSessionUuid: string | null;
  reason: EscalationReason;
  summary: string;
  // Set when the backend created an Intercom conversation on the user's
  // behalf via the support_escalation tool's REST proxy. FE opens that
  // thread directly via showConversation. Null when the backend proxy
  // failed — FE falls back to showNewMessage(prefill).
  conversationId: string | null;
}

const IDLE_TIMEOUT_MS = 120_000;

// Mirrors backend's HEARTBEAT_MAX_DURATION_S (BE-1411).
const HEARTBEAT_MAX_DURATION_MS = 600_000;

const ACCEPTED_MIME_TO_EXT: Record<string, string> = {
  "application/pdf": "pdf",
  "application/msword": "doc",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
    "docx",
  "application/vnd.ms-excel": "xls",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet":
    "xlsx",
  "image/jpeg": "jpg",
  "image/png": "png",
  "image/webp": "webp",
  "image/gif": "gif",
  "image/svg+xml": "svg",
  "text/csv": "csv",
  "text/plain": "txt",
};
const ALLOWED_EXTENSIONS = new Set([
  ...Object.values(ACCEPTED_MIME_TO_EXT),
  // jpeg filename fallback — "image/jpeg" maps to "jpg" above.
  "jpeg",
]);
export const AGENT_ATTACHMENT_ACCEPT =
  ".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.webp,.gif,.svg,.csv,.txt";
export const MAX_ATTACHMENT_SIZE_MB = 25;
export const MAX_ATTACHMENTS_PER_MESSAGE = 10;

function extensionForFile(file: File): string | null {
  const fromMime = ACCEPTED_MIME_TO_EXT[file.type];
  if (fromMime) return fromMime;
  const ext = file.name.split(".").pop()?.toLowerCase() ?? "";
  return ALLOWED_EXTENSIONS.has(ext) ? ext : null;
}

function parseCompanionList(value: unknown): CompanionIdentity[] {
  return Array.isArray(value) ? normalizeCompanionList(value) : [];
}

function sanitizeFileLink(value: unknown): string | null {
  if (typeof value !== "string" || !value) return null;
  try {
    const parsed = new URL(value);
    return parsed.protocol === "https:" || parsed.protocol === "http:"
      ? parsed.toString()
      : null;
  } catch {
    return null;
  }
}

// Returns same `existing` ref when no-op, so callers can short-circuit setState.
function mergeAttachmentsByUuid(
  existing: CompanionV2Attachment[] | undefined,
  incoming: CompanionV2Attachment[],
): CompanionV2Attachment[] | undefined {
  if (!incoming.length) return existing;
  const base = existing ?? [];
  let merged: CompanionV2Attachment[] | null = null;
  for (const a of incoming) {
    if (base.some((x) => x.uuid === a.uuid)) continue;
    if (merged === null) merged = [...base];
    merged.push(a);
  }
  return merged ?? existing;
}

function parseToolPermissionPayload(
  record: CompanionToolPermissionRequestData,
): ToolPermissionPayload {
  return createPermissionPayload({
    requestId: record.request_id,
    agentSessionUuid: record.agent_session_uuid,
    toolName: record.tool_name,
    toolAction: record.tool_action ?? "",
    toolKind: record.tool_kind,
    title: record.title,
    summary: record.summary,
    details: (record.details ?? []).slice(0, 6),
    canPersist: record.can_persist ?? false,
    persistLabel: record.persist_label?.trim() || "Always Allow",
    grantDisplayName: record.grant_display_name ?? "",
    expiresAt: record.expires_at ?? null,
    status: record.status ?? "pending",
  });
}

function permissionPayloadFromHydrated(record: {
  requestId: string;
  agentSessionUuid: string;
  toolName: string;
  toolAction: string;
  toolKind: string;
  title: string;
  summary: string;
  canPersist: boolean;
  persistLabel: string;
  grantDisplayName: string;
  status: string | null;
  expiresAt: string | null;
  details: ReadonlyArray<{ label: string; value: string }>;
}): ToolPermissionPayload {
  return createPermissionPayload({
    requestId: record.requestId,
    agentSessionUuid: record.agentSessionUuid,
    toolName: record.toolName,
    toolAction: record.toolAction,
    toolKind: record.toolKind as "builtin" | "custom",
    title: record.title,
    summary: record.summary,
    details: record.details.slice(0, 6).map((d) => ({
      label: d.label,
      value: d.value,
    })),
    canPersist: record.canPersist,
    persistLabel: record.persistLabel.trim() || "Always Allow",
    grantDisplayName: record.grantDisplayName,
    expiresAt: record.expiresAt,
    status: record.status === "expired" ? "expired" : "pending",
  });
}

export interface CompanionV2Attachment {
  uuid: string;
  fileName: string;
  contentType: string;
  fileLink: string | null;
  source: "user" | "agent";
  eventId: string | null;
}

export interface PendingAttachment {
  uuid: string;
  fileName: string;
  contentType: string;
}

export type AttachmentStageError =
  | { kind: "unsupported-type" }
  | { kind: "too-large"; maxMB: number }
  | { kind: "cap-reached"; max: number }
  | { kind: "upload-failed"; message: string };

export interface CompanionV2Message {
  id: string;
  role: "user" | "assistant";
  kind?: "text" | "nav_proposal" | "tool_permission" | "interrupt";
  text: string;
  timestamp: Date;
  toolCalls?: ToolCall[];
  isError?: boolean;
  // Ordered list of companions that contributed to the turn. Populated
  // incrementally by `companion-tool-use` and finalized (authoritative)
  // by `companion-turn-end`. Rendered as one avatar card per entry.
  companions?: CompanionIdentity[];
  navProposal?: NavPayload;
  toolPermission?: ToolPermissionPayload;
  attachments?: CompanionV2Attachment[];
}

export interface CompanionTooltipAction {
  label: string;
  message?: string;
  key?: string;
  onClick?: () => void;
}

export interface CompanionTooltipTracking {
  trigger: string;
  properties?: Record<string, unknown>;
}

export interface CompanionTooltip {
  id: string;
  companions?: CompanionIdentity[];
  content: ReactNode;
  actions: CompanionTooltipAction[];
  onDismiss?: () => void;
}

export interface ShowCompanionTooltipInput {
  id?: string;
  companions?: CompanionIdentity[];
  content: ReactNode;
  actions?: CompanionTooltipAction[];
  onDismiss?: () => void;
  tracking?: CompanionTooltipTracking;
}

interface CompanionV2ContextValue {
  messages: CompanionV2Message[];
  isStreaming: boolean;
  isCreatingSession: boolean;
  agentSessionUuid: string | null;
  isResumedSession: boolean;
  error: CompanionV2MessageError | null;
  isHydrating: boolean;
  hydrationError: string | null;
  hasMoreHistory: boolean;
  isLoadingMoreHistory: boolean;
  hasPendingToolPermission: boolean;
  effort: CompanionEffort;
  setEffort: (effort: CompanionEffort) => void;
  pendingAttachments: PendingAttachment[];
  isStagingAttachment: boolean;
  isBackgrounded: boolean;
  isStopping: boolean;
  sendMessage: (text: string) => Promise<void>;
  stopGeneration: () => Promise<void>;
  retry: () => Promise<void>;
  clearError: () => void;
  clearSession: () => void;
  loadSession: (agentSessionUuid: string) => void;
  loadMoreHistory: () => Promise<void>;
  retryHydration: () => Promise<void>;
  acceptNavProposal: (messageId: string) => void;
  dismissNavProposal: (messageId: string) => void;
  decideToolPermission: (
    messageId: string,
    decision: ToolPermissionDecision,
    remember?: boolean,
  ) => Promise<void>;
  stageAttachment: (
    file: File,
  ) => Promise<AttachmentStageError | null>;
  removePendingAttachment: (uuid: string) => void;
  escalationRequest: EscalationRequest | null;
  clearEscalationRequest: () => void;
  tooltip: CompanionTooltip | null;
  showCompanionTooltip: (input: ShowCompanionTooltipInput) => void;
  dismissCompanionTooltip: () => void;
}

type CompanionV2MessageError = {
  content: string | null;
  canRetry: boolean;
  isOutOfTreats?: boolean;
};

const CompanionV2Context =
  createContext<CompanionV2ContextValue | null>(null);

export function CompanionV2Provider({
  children,
}: {
  children: React.ReactNode;
}) {
  const value = useCompanionV2Internal();
  return (
    <CompanionV2Context.Provider value={value}>
      {children}
    </CompanionV2Context.Provider>
  );
}

export function useCompanionV2(): CompanionV2ContextValue {
  const ctx = useContext(CompanionV2Context);
  if (!ctx) {
    throw new Error(
      "useCompanionV2 must be used within CompanionV2Provider",
    );
  }
  return ctx;
}

function useCompanionV2Internal(): CompanionV2ContextValue {
  const {
    allowCompanionOptIn,
    showGlobalCompanionTierLow,
    showGlobalCompanionTierHigh,
  } = useFlags();
  const companionTooltipsDisabled = useCompanionTooltipsDisabled();
  const defaultEffort = useMemo<CompanionEffort>(() => {
    if (!allowCompanionOptIn) return "support";
    if (showGlobalCompanionTierHigh) return "high";
    if (showGlobalCompanionTierLow) return "standard";
    return "support";
  }, [
    allowCompanionOptIn,
    showGlobalCompanionTierHigh,
    showGlobalCompanionTierLow,
  ]);

  const [hydratedEvents, setHydratedEvents] = useState<RawEvent[]>(
    [],
  );
  const [hydratedPendingPermissions, setHydratedPendingPermissions] =
    useState<ToolPermissionPayload[]>([]);
  const [liveMessages, setLiveMessages] = useState<
    CompanionV2Message[]
  >([]);
  const [isStreaming, setIsStreaming] = useState(false);
  const [isCreatingSession, setIsCreatingSession] = useState(false);
  const [agentSessionUuid, setAgentSessionUuid] = useState<
    string | null
  >(null);
  const [isResumedSession, setIsResumedSession] = useState(false);
  const [effort, setEffort] =
    useState<CompanionEffort>(defaultEffort);
  const [error, setError] = useState<CompanionV2MessageError | null>(
    null,
  );
  const [isHydrating, setIsHydrating] = useState(false);
  const [hydrationError, setHydrationError] = useState<string | null>(
    null,
  );
  const [nextHistoryCursor, setNextHistoryCursor] = useState<
    string | null
  >(null);
  const [isLoadingMoreHistory, setIsLoadingMoreHistory] =
    useState(false);
  const [pendingAttachments, _setPendingAttachments] = useState<
    PendingAttachment[]
  >([]);
  const setPendingAttachments = useCallback(
    (
      next:
        | PendingAttachment[]
        | ((prev: PendingAttachment[]) => PendingAttachment[]),
    ) => {
      const value =
        typeof next === "function"
          ? next(pendingAttachmentsRef.current)
          : next;
      pendingAttachmentsRef.current = value;
      _setPendingAttachments(value);
    },
    [],
  );
  const [isStagingAttachment, setIsStagingAttachment] =
    useState(false);
  const [sessionAttachments, setSessionAttachments] = useState<
    CompanionV2Attachment[]
  >([]);
  const [isBackgrounded, setIsBackgrounded] = useState(false);
  const [isStopping, setIsStopping] = useState(false);
  const [escalationRequest, setEscalationRequest] =
    useState<EscalationRequest | null>(null);

  const attachmentsByEventId = useMemo(() => {
    const map = new Map<string, CompanionV2Attachment[]>();
    for (const a of sessionAttachments) {
      if (!a.eventId) continue;
      const arr = map.get(a.eventId) ?? [];
      arr.push(a);
      map.set(a.eventId, arr);
    }
    return map;
  }, [sessionAttachments]);

  const [clientResolvedToolUseIds, setClientResolvedToolUseIds] =
    useState<ReadonlySet<string>>(() => new Set());

  const [seededTrailingToolUseIds, setSeededTrailingToolUseIds] =
    useState<ReadonlySet<string>>(() => new Set());

  // request_id equals the tool_use event id.
  const expiredToolUseIds = useMemo(
    () =>
      new Set(
        hydratedPendingPermissions
          .filter((p) => p.status === "expired")
          .map((p) => p.requestId),
      ),
    [hydratedPendingPermissions],
  );

  const hydratedMessages = useMemo(() => {
    const filtered = seededTrailingToolUseIds.size
      ? hydratedEvents.filter(
          (e) => !seededTrailingToolUseIds.has(e.id),
        )
      : hydratedEvents;
    return mapEventsToMessages(
      filtered,
      attachmentsByEventId,
      clientResolvedToolUseIds,
      expiredToolUseIds,
    );
  }, [
    hydratedEvents,
    attachmentsByEventId,
    clientResolvedToolUseIds,
    expiredToolUseIds,
    seededTrailingToolUseIds,
  ]);
  const messages = useMemo(
    () => [...hydratedMessages, ...liveMessages],
    [hydratedMessages, liveMessages],
  );

  const suppressedHydratedRequestIdsRef = useRef<Set<string>>(
    new Set(),
  );

  const hasPendingToolPermission = useMemo(
    () =>
      messages.some(
        (m) =>
          m.kind === "tool_permission" &&
          m.toolPermission?.status === "pending",
      ),
    [messages],
  );

  useEffect(() => {
    if (!agentSessionUuid || isHydrating) return;
    if (hydratedPendingPermissions.length === 0) return;
    setLiveMessages((prev) => {
      const existingRequestIds = new Set<string>();
      for (const m of prev) {
        if (m.toolPermission?.requestId) {
          existingRequestIds.add(m.toolPermission.requestId);
        }
      }
      const suppressed = suppressedHydratedRequestIdsRef.current;
      const cards = hydratedPendingPermissions
        .filter(
          (p) =>
            !existingRequestIds.has(p.requestId) &&
            !suppressed.has(p.requestId),
        )
        .map<CompanionV2Message>((p) => ({
          id: `tool-permission-${p.requestId}`,
          role: "assistant",
          kind: "tool_permission",
          text: "",
          timestamp: new Date(),
          toolPermission: p,
        }));
      return cards.length > 0 ? [...prev, ...cards] : prev;
    });
  }, [hydratedPendingPermissions, agentSessionUuid, isHydrating]);

  const [fetchAgentSessionEvents] = useLazyQuery(
    GET_AGENT_SESSION_EVENTS,
    { fetchPolicy: "network-only" },
  );
  const [fetchAgentSessionAttachments] = useLazyQuery(
    GET_AGENT_SESSION_ATTACHMENTS,
    { fetchPolicy: "network-only" },
  );
  const [uploadAgentSessionAttachment] = useMutation(
    UPLOAD_AGENT_SESSION_ATTACHMENT,
  );
  const { connectWS, webSockets } = useWS();
  const { userUuid, isUserAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const routeKey = `${location.pathname}${location.search}`;
  const apolloClient = useApolloClient();

  const lastUserMessageRef = useRef<string | null>(null);
  const lastTurnAttachmentsRef = useRef<PendingAttachment[]>([]);
  const msgIdRef = useRef(0);
  const nextId = () => `msg-${++msgIdRef.current}`;

  // Turn-scoped: one turn = one user send, zero or more assistant
  // bubbles (one per companion-message event). Each bubble keyed on
  // agent_message_id so replayed events dedupe.
  const turnInFlightRef = useRef(false);
  const turnBubbleIdsRef = useRef<string[]>([]);
  // Placeholder bubble sits at the end of the list while a turn is in
  // flight. It shows the thinking-dots / tool-badges state between real
  // bubbles and is removed on turn-end.
  const placeholderIdRef = useRef<string | null>(null);
  // Tools seen since the last companion-message — flushed onto the
  // next bubble as its tool-status chain. Per-bubble scope. Each
  // entry's state moves running → done as subsequent tool-use events
  // arrive (only one tool runs at a time), or → error on turn-error.
  const pendingToolCallsRef = useRef<ToolCall[]>([]);
  // Turn-scoped cumulative set of personas. Never cleared mid-turn, so
  // every bubble in the turn shares the same avatar stack — when a new
  // persona appears, prior bubbles in the turn are updated too.
  const turnPersonasRef = useRef<CompanionIdentity[]>([]);
  const streamResolveRef = useRef<(() => void) | null>(null);
  const streamRejectRef = useRef<((e: Error) => void) | null>(null);
  // Nav proposals are buffered until companion-turn-end so confirm cards
  // land after the agent's messages.
  const pendingProposalsRef = useRef<NavPayload[]>([]);
  const turnRouteRef = useRef<string | null>(null);
  const resolvingPermissionIdsRef = useRef<Set<string>>(new Set());
  // Same deferral for support-escalation — Intercom should pop up after
  // Otto's final hand-off reply finishes rendering, not mid-stream.
  const pendingEscalationRef = useRef<{
    reason: EscalationReason;
    summary: string;
    conversationId: string | null;
  } | null>(null);
  // Attachment events can outpace the owning companion-message; buffer by
  // agent_message_id and flush when the bubble lands.
  const pendingAgentAttachmentsRef = useRef<
    Map<string, CompanionV2Attachment[]>
  >(new Map());

  const bindAgentAttachment = useCallback(
    (
      attachment: CompanionV2Attachment,
      agentMessageId: string | null,
    ) => {
      const appendToBubble = (targetId: string) =>
        setLiveMessages((prev) => {
          const idx = prev.findIndex((m) => m.id === targetId);
          if (idx === -1) return prev;
          const merged = mergeAttachmentsByUuid(
            prev[idx].attachments,
            [attachment],
          );
          if (merged === prev[idx].attachments) return prev;
          const next = [...prev];
          next[idx] = { ...prev[idx], attachments: merged };
          return next;
        });

      if (agentMessageId) {
        if (turnBubbleIdsRef.current.includes(agentMessageId)) {
          appendToBubble(agentMessageId);
          return;
        }
        const buf =
          pendingAgentAttachmentsRef.current.get(agentMessageId);
        if (!buf) {
          pendingAgentAttachmentsRef.current.set(agentMessageId, [
            attachment,
          ]);
        } else if (!buf.some((a) => a.uuid === attachment.uuid)) {
          buf.push(attachment);
        }
        return;
      }

      // No agent_message_id (tool-only turn) → fall back to last bubble.
      const fallbackId = turnBubbleIdsRef.current.at(-1);
      if (fallbackId) appendToBubble(fallbackId);
    },
    [],
  );

  const updatePermissionMessage = useCallback(
    (
      match: (m: CompanionV2Message) => boolean,
      updater: (p: ToolPermissionPayload) => ToolPermissionPayload,
    ) => {
      setLiveMessages((prev) =>
        prev.map((m) =>
          m.toolPermission && match(m)
            ? { ...m, toolPermission: updater(m.toolPermission) }
            : m,
        ),
      );
    },
    [],
  );

  const resetTurnRefs = useCallback(() => {
    turnInFlightRef.current = false;
    turnBubbleIdsRef.current = [];
    placeholderIdRef.current = null;
    pendingToolCallsRef.current = [];
    turnPersonasRef.current = [];
    pendingProposalsRef.current = [];
    turnRouteRef.current = null;
    resolvingPermissionIdsRef.current.clear();
    pendingEscalationRef.current = null;
    pendingAgentAttachmentsRef.current.clear();
  }, []);

  const idleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const turnStartedAtRef = useRef<number | null>(null);

  const activeSessionRef = useRef<string | null>(null);
  const sessionPromiseRef = useRef<Promise<string> | null>(null);
  const stagingInFlightRef = useRef(0);
  // Mirrors pendingAttachments so sendMessage / clearSession read the
  // synchronous-current value. Without this, a synchronous clearSession
  // followed by sendMessage in the same handler would see a stale
  // attachments array (state batches until after the handler returns)
  // and could ship stale attachments with the next turn.
  const pendingAttachmentsRef = useRef<PendingAttachment[]>([]);

  const clearError = useCallback(() => setError(null), []);
  const clearEscalationRequest = useCallback(
    () => setEscalationRequest(null),
    [],
  );

  // Drains a queued support-escalation request at `companion-turn-end`,
  // seeding the Intercom prefill with the just-finalized last user/
  // assistant turn. Wrapped in `useEffectEvent` so the WS handler
  // effect can call it without depending on `liveMessages` (which would
  // re-attach the listener on every chunk).
  const flushPendingEscalation = useEffectEvent(() => {
    if (!pendingEscalationRef.current) return;
    const { reason, summary, conversationId } =
      pendingEscalationRef.current;
    pendingEscalationRef.current = null;
    let lastUser: string | null = null;
    let lastAssistant: string | null = null;
    for (let i = liveMessages.length - 1; i >= 0; i--) {
      const m = liveMessages[i];
      if (m.kind === "nav_proposal") continue;
      if (!lastAssistant && m.role === "assistant" && m.text) {
        lastAssistant = m.text;
      }
      if (!lastUser && m.role === "user" && m.text) {
        lastUser = m.text;
      }
      if (lastUser && lastAssistant) break;
    }
    setEscalationRequest({
      lastUserMessage: lastUser,
      lastAssistantMessage: lastAssistant,
      agentSessionUuid: activeSessionRef.current,
      reason,
      summary,
      conversationId,
    });
  });

  const clearIdleTimer = useCallback(() => {
    if (idleTimerRef.current !== null) {
      clearTimeout(idleTimerRef.current);
      idleTimerRef.current = null;
    }
  }, []);

  const scheduleIdleTimer = useCallback(() => {
    clearIdleTimer();
    idleTimerRef.current = setTimeout(() => {
      idleTimerRef.current = null;
      const startedAt = turnStartedAtRef.current;
      const elapsed = startedAt !== null ? Date.now() - startedAt : 0;
      if (elapsed >= HEARTBEAT_MAX_DURATION_MS) {
        setIsBackgrounded(true);
        streamResolveRef.current?.();
      } else {
        streamRejectRef.current?.(new Error("Response timed out"));
      }
    }, IDLE_TIMEOUT_MS);
  }, [clearIdleTimer]);

  useEffect(() => clearIdleTimer, [clearIdleTimer]);

  const armTurn = useCallback(
    (seed?: {
      toolCalls?: ToolCall[];
      personas?: CompanionIdentity[];
    }): {
      streamPromise: Promise<void>;
      resolve: () => void;
      disarm: () => void;
    } => {
      const armedSessionUuid = activeSessionRef.current;
      resetTurnRefs();
      turnInFlightRef.current = true;
      turnRouteRef.current = routeKey;
      setIsStreaming(true);

      if (seed?.toolCalls?.length) {
        pendingToolCallsRef.current = seed.toolCalls;
      }
      if (seed?.personas?.length) {
        turnPersonasRef.current = [...seed.personas];
      }

      const placeholderId = nextId();
      placeholderIdRef.current = placeholderId;
      setLiveMessages((prev) => [
        ...prev,
        {
          id: placeholderId,
          role: "assistant",
          text: "",
          timestamp: new Date(),
          toolCalls: seed?.toolCalls?.length
            ? seed.toolCalls
            : undefined,
          companions: seed?.personas?.length
            ? seed.personas
            : undefined,
        },
      ]);

      let resolve!: () => void;
      let reject!: (e: Error) => void;
      const streamPromise = new Promise<void>((res, rej) => {
        resolve = res;
        reject = rej;
      });
      streamResolveRef.current = resolve;
      streamRejectRef.current = reject;
      turnStartedAtRef.current = Date.now();
      scheduleIdleTimer();

      const disarm = () => {
        if (activeSessionRef.current !== armedSessionUuid) return;
        streamResolveRef.current = null;
        streamRejectRef.current = null;
        const leftoverId = placeholderIdRef.current;
        if (leftoverId) {
          setLiveMessages((prev) =>
            prev.filter((m) => m.id !== leftoverId),
          );
        }
        resetTurnRefs();
        clearIdleTimer();
        turnStartedAtRef.current = null;
        setIsStreaming(false);
      };

      return { streamPromise, resolve, disarm };
    },
    [resetTurnRefs, routeKey, scheduleIdleTimer, clearIdleTimer],
  );

  const channel = userUuid ? `user/${userUuid}` : null;
  const userWs = channel ? webSockets[channel] : null;

  useEffect(() => {
    if (channel && !userWs) {
      connectWS(channel);
    }
  }, [channel, userWs, connectWS]);

  // Raw addEventListener: multi-event dispatch + turn-coupled refs don't fit useWatchUserWsEvent's single-event API.
  useEffect(() => {
    if (!userWs) return;

    const handler = (e: MessageEvent) => {
      let parsed: AnyWebSocketEvent;
      try {
        parsed = JSON.parse(e.data) as AnyWebSocketEvent;
      } catch {
        return;
      }

      if (!isCompanionWsEvent(parsed)) return;

      // Session filter: drop events for other tabs' sessions, and drop
      // any session-scoped event once this tab has no active session
      // (post-clearSession / pre-session-create).
      const eventSessionUuid = parsed.data.agent_session_uuid;
      if (
        eventSessionUuid &&
        eventSessionUuid !== activeSessionRef.current
      ) {
        return;
      }

      if (!turnInFlightRef.current) return;

      scheduleIdleTimer();
      if (parsed.event === "companion-heartbeat") return;

      if (parsed.event === "companion-message") {
        const { content, agent_message_id: bubbleId } = parsed.data;
        if (!content) return;

        // Any tool still marked running flips to done — the bubble
        // landing means the agent finished its tool phase.
        const tools = pendingToolCallsRef.current.map((c) =>
          c.state === "running"
            ? { ...c, state: "done" as const }
            : c,
        );
        pendingToolCallsRef.current = [];
        // Snapshot of cumulative turn personas at the moment this
        // bubble lands. Personas that appear later in the turn are
        // not back-propagated onto this bubble.
        const personas = [...turnPersonasRef.current];
        turnBubbleIdsRef.current.push(bubbleId);

        const placeholderId = placeholderIdRef.current;
        const buffered =
          pendingAgentAttachmentsRef.current.get(bubbleId);
        if (buffered)
          pendingAgentAttachmentsRef.current.delete(bubbleId);
        setLiveMessages((prev) => {
          const existingIdx = prev.findIndex(
            (m) => m.id === bubbleId,
          );
          if (existingIdx !== -1) {
            if (!buffered?.length) return prev;
            const existing = prev[existingIdx];
            const merged = mergeAttachmentsByUuid(
              existing.attachments,
              buffered,
            );
            if (merged === existing.attachments) return prev;
            const next = [...prev];
            next[existingIdx] = { ...existing, attachments: merged };
            return next;
          }
          const newBubble: CompanionV2Message = {
            id: bubbleId,
            role: "assistant",
            text: content,
            timestamp: new Date(),
            toolCalls: tools.length ? tools : undefined,
            companions: personas.length ? personas : undefined,
            attachments: buffered?.length ? buffered : undefined,
          };
          // Insert before the placeholder so thinking-dots stay at the
          // bottom; clear the tool-call badges the placeholder was
          // showing (they've now moved onto the new bubble). Keep the
          // placeholder's persona avatars so users still see which
          // companions are active.
          const phIdx = placeholderId
            ? prev.findIndex((m) => m.id === placeholderId)
            : -1;
          if (phIdx === -1) return [...prev, newBubble];
          const next = [...prev];
          next.splice(phIdx, 0, newBubble);
          next[phIdx + 1] = {
            ...next[phIdx + 1],
            toolCalls: undefined,
          };
          return next;
        });
      } else if (parsed.event === "companion-tool-use") {
        const {
          tool_name: toolName,
          tool_action: toolAction,
          companion_type,
        } = parsed.data;
        const persona = normalizeCompanionType(companion_type);
        if (toolName && !shouldHideToolUse(toolName, toolAction)) {
          // Only one tool runs at a time — flip any prior running
          // entry to done before appending the new one.
          pendingToolCallsRef.current =
            pendingToolCallsRef.current.map((c) =>
              c.state === "running"
                ? { ...c, state: "done" as const }
                : c,
            );
          pendingToolCallsRef.current.push({
            id: nextId(),
            tool: toolName,
            action: toolAction ?? undefined,
            state: "running",
            timestamp: new Date(),
            companion: persona,
          });
        }
        if (persona && !turnPersonasRef.current.includes(persona)) {
          turnPersonasRef.current.push(persona);
        }
        // Mirror cumulative tools + personas onto the placeholder only;
        // already-landed bubbles keep their original snapshot.
        const placeholderId = placeholderIdRef.current;
        if (!placeholderId) return;
        const tools = [...pendingToolCallsRef.current];
        const personas = [...turnPersonasRef.current];
        setLiveMessages((prev) =>
          prev.map((m) =>
            m.id === placeholderId
              ? {
                  ...m,
                  toolCalls: tools.length ? tools : undefined,
                  companions: personas.length ? personas : undefined,
                }
              : m,
          ),
        );
      } else if (parsed.event === "companion-attachment") {
        if (parsed.data.source === "user") return;
        bindAgentAttachment(
          {
            uuid: parsed.data.uuid,
            fileName: parsed.data.file_name,
            contentType: parsed.data.content_type,
            fileLink: sanitizeFileLink(parsed.data.file_link),
            source: "agent",
            eventId: null,
          },
          parsed.data.agent_message_id ?? null,
        );
      } else if (parsed.event === "companion-turn-end") {
        // Fallback-only: if a bubble somehow ended up with no
        // attribution (e.g. tool-use events lacked companion_type),
        // seed it from the authoritative turn union. Bubbles that
        // already have companions keep their forward-only snapshot.
        const authoritative = parseCompanionList(
          parsed.data.companions_used,
        );
        const turnIds = new Set(turnBubbleIdsRef.current);
        const placeholderId = placeholderIdRef.current;
        placeholderIdRef.current = null;
        const pendingAttachments = pendingAgentAttachmentsRef.current;
        pendingAgentAttachmentsRef.current = new Map();
        setLiveMessages((prev) =>
          prev
            .filter(
              (m) =>
                m.id !== placeholderId &&
                !(
                  m.kind === "tool_permission" &&
                  m.toolPermission?.status === "approved"
                ),
            )
            .map((m) => {
              let next = m;
              if (
                authoritative.length > 0 &&
                turnIds.has(m.id) &&
                !(m.companions?.length ?? 0)
              ) {
                next = { ...next, companions: authoritative };
              }
              const buffered = pendingAttachments.get(m.id);
              if (buffered?.length) {
                const merged = mergeAttachmentsByUuid(
                  next.attachments,
                  buffered,
                );
                if (merged !== next.attachments) {
                  next = { ...next, attachments: merged };
                }
              }
              return next;
            }),
        );
        if (pendingProposalsRef.current.length > 0) {
          const proposals = pendingProposalsRef.current;
          pendingProposalsRef.current = [];
          setLiveMessages((prev) => [
            ...prev,
            ...proposals.map((proposal) => ({
              id: nextId(),
              role: "assistant" as const,
              kind: "nav_proposal" as const,
              text: "",
              timestamp: new Date(),
              navProposal: proposal,
            })),
          ]);
        }
        flushPendingEscalation();
        streamResolveRef.current?.();
      } else if (parsed.event === "companion-turn-error") {
        const authoritative = parseCompanionList(
          parsed.data.companions_used,
        );
        const turnIds = new Set(turnBubbleIdsRef.current);
        const placeholderId = placeholderIdRef.current;
        const hadAnyBubbles = turnIds.size > 0;
        placeholderIdRef.current = null;
        // Mark the active running tool as errored so the chain in the
        // error bubble (no-bubbles path) reflects what failed.
        const failedChain = pendingToolCallsRef.current.map((c) =>
          c.state === "running"
            ? { ...c, state: "error" as const }
            : c,
        );
        pendingToolCallsRef.current = [];
        setLiveMessages((prev) => {
          const withAttribution =
            authoritative.length > 0
              ? prev.map((m) =>
                  turnIds.has(m.id) && !(m.companions?.length ?? 0)
                    ? { ...m, companions: authoritative }
                    : m,
                )
              : prev;
          // No real bubbles landed — convert placeholder into an error
          // bubble so the user sees something went wrong. Keep the
          // tool chain so they can review which step failed.
          if (!hadAnyBubbles && placeholderId) {
            return withAttribution.map((m) =>
              m.id === placeholderId
                ? {
                    ...m,
                    text: "Sorry, something went wrong. You can try again.",
                    isError: true,
                    toolCalls: failedChain.length
                      ? failedChain
                      : undefined,
                    companions: undefined,
                  }
                : m,
            );
          }
          return withAttribution.filter(
            (m) => m.id !== placeholderId,
          );
        });
        pendingProposalsRef.current = [];
        pendingEscalationRef.current = null;
        streamRejectRef.current?.(new Error("Something went wrong"));
      } else if (
        parsed.event === "companion-tool-permission-request"
      ) {
        const toolPayload = parseToolPermissionPayload(parsed.data);
        setLiveMessages((prev) => {
          const existing = prev.find(
            (m) =>
              m.toolPermission?.requestId === toolPayload.requestId,
          );
          if (existing) {
            return prev.map((m) =>
              m.toolPermission?.requestId === toolPayload.requestId
                ? { ...m, toolPermission: toolPayload }
                : m,
            );
          }
          return [
            ...prev,
            {
              id: `tool-permission-${toolPayload.requestId}`,
              role: "assistant" as const,
              kind: "tool_permission" as const,
              text: "",
              timestamp: new Date(),
              toolPermission: toolPayload,
            },
          ];
        });
      } else if (
        parsed.event === "companion-tool-permission-resolved"
      ) {
        const requestId = parsed.data.request_id;
        const sessionUuid = parsed.data.agent_session_uuid;
        const status = parsed.data.status;
        suppressedHydratedRequestIdsRef.current.add(requestId);
        // Evict from cache so a remount doesn't replay the dismissed card.
        apolloClient.cache.updateQuery(
          {
            query: GET_AGENT_SESSION_EVENTS,
            variables: {
              agentSessionUuid: sessionUuid,
              after: null,
            },
          },
          (data) => {
            if (!data?.agentSessionEvents) return data;
            const filtered =
              data.agentSessionEvents.pendingPermissions.filter(
                (p) => p.requestId !== requestId,
              );
            if (
              filtered.length ===
              data.agentSessionEvents.pendingPermissions.length
            ) {
              return data;
            }
            return {
              ...data,
              agentSessionEvents: {
                ...data.agentSessionEvents,
                pendingPermissions: filtered,
              },
            };
          },
        );
        updatePermissionMessage(
          (m) => m.toolPermission?.requestId === requestId,
          (p) => ({ ...p, status, isResolving: false, error: null }),
        );
      } else if (parsed.event === "companion-nav-proposal") {
        const { entity_type, entity_uuid, path, label, reason } =
          parsed.data;
        const kind: NavKind =
          entity_type === "note" ||
          entity_type === "patient" ||
          entity_type === "template" ||
          entity_type === "page"
            ? entity_type
            : "page";
        pendingProposalsRef.current.push({
          kind,
          path,
          label,
          reason,
          entity_uuid,
        });
      } else if (parsed.event === "companion-nav-execute") {
        navigate(parsed.data.path);
      } else if (parsed.event === "companion-escalate-support") {
        // Defer to companion-turn-end so Intercom pops up after Otto's
        // hand-off reply finishes rendering, not mid-stream.
        pendingEscalationRef.current = {
          reason: parsed.data.reason,
          summary: parsed.data.summary,
          conversationId: parsed.data.conversation_id ?? null,
        };
      } else {
        // Exhaustiveness: unhandled companion-* events must fail tsc.
        const _exhaustive: never = parsed;
        void _exhaustive;
      }
    };

    userWs.addEventListener("message", handler);
    return () => userWs.removeEventListener("message", handler);
  }, [
    userWs,
    navigate,
    scheduleIdleTimer,
    updatePermissionMessage,
    bindAgentAttachment,
    apolloClient,
  ]);

  useWatchUserWsEvent({
    eventName: "companion-session-title-update",
    onMessage: () => {
      apolloClient.refetchQueries({ include: [GET_AGENT_SESSIONS] });
    },
  });

  const createSession = useCallback(async (): Promise<string> => {
    try {
      const res = await fetch(`${AGENT_API}/session`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ effort }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(
          body.detail ||
            `Could not start session (HTTP ${res.status})`,
        );
      }
      const data: { agent_session_uuid?: string } = await res.json();
      const uuid = data.agent_session_uuid;
      if (!uuid) {
        throw new Error(
          "Session response missing agent_session_uuid",
        );
      }
      activeSessionRef.current = uuid;
      setAgentSessionUuid(uuid);
      setIsResumedSession(false);
      return uuid;
    } finally {
      sessionPromiseRef.current = null;
      setIsCreatingSession(false);
    }
  }, [effort]);

  const ensureSession = useCallback(async (): Promise<string> => {
    // Prefer refs over state — state lags one render after clearSession,
    // so a synchronous clearSession + sendMessage in the same handler
    // would otherwise resolve to the just-cleared session uuid.
    if (activeSessionRef.current) return activeSessionRef.current;
    if (sessionPromiseRef.current) return sessionPromiseRef.current;

    setIsCreatingSession(true);
    const pending = createSession();
    sessionPromiseRef.current = pending;
    return pending;
  }, [createSession]);

  const performSend = useCallback(
    async (text: string, turnAttachments: PendingAttachment[]) => {
      if (isStreaming) return;

      lastUserMessageRef.current = text;
      lastTurnAttachmentsRef.current = turnAttachments;

      let currentSessionId: string;
      try {
        currentSessionId = await ensureSession();
      } catch (e) {
        setError({
          content: `Failed to connect: ${(e as Error).message}. Try again.`,
          canRetry: true,
        });
        return;
      }

      const attachmentsForBubble: CompanionV2Attachment[] =
        turnAttachments.map((a) => ({
          uuid: a.uuid,
          fileName: a.fileName,
          contentType: a.contentType,
          fileLink: null,
          source: "user",
          eventId: null,
        }));
      setLiveMessages((prev) => [
        ...prev,
        {
          id: nextId(),
          role: "user",
          text,
          timestamp: new Date(),
          attachments: attachmentsForBubble.length
            ? attachmentsForBubble
            : undefined,
        },
      ]);
      setError(null);
      setIsBackgrounded(false);

      const { streamPromise, disarm } = armTurn();

      try {
        const res = await fetch(`${AGENT_API}/chat`, {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            agent_session_uuid: currentSessionId,
            message: text,
            current_path: window.location.pathname,
            attachment_uuids: turnAttachments.map((a) => a.uuid),
            message_sent_at: moment().format(),
          }),
        });

        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          throw new HttpError(
            body.detail || `Request failed (HTTP ${res.status})`,
            res.status,
          );
        }

        await streamPromise;
      } catch (e) {
        const err = e as Error;
        setError({
          content: err.message,
          canRetry: err instanceof HttpError ? err.canRetry : true,
          isOutOfTreats:
            err instanceof HttpError && err.isOutOfTreats,
        });
        const placeholderId = placeholderIdRef.current;
        const hadAnyBubbles = turnBubbleIdsRef.current.length > 0;
        if (!hadAnyBubbles && placeholderId) {
          setLiveMessages((prev) =>
            prev.map((m) =>
              m.id === placeholderId
                ? {
                    ...m,
                    text: "Sorry, something went wrong. You can try again.",
                    isError: true,
                    toolCalls: undefined,
                    companions: undefined,
                  }
                : m,
            ),
          );
        } else if (placeholderId) {
          setLiveMessages((prev) =>
            prev.filter((m) => m.id !== placeholderId),
          );
        }
      } finally {
        disarm();
      }
    },
    [isStreaming, ensureSession, armTurn],
  );

  const stopGeneration = useCallback(async () => {
    const sessionUuid = activeSessionRef.current;
    if (!sessionUuid || !streamResolveRef.current) return;
    setIsStopping(true);
    try {
      const res = await fetch(
        `${AGENT_API}/session/${sessionUuid}/interrupt`,
        {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
        },
      );
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(
          body.detail || `Could not stop (HTTP ${res.status})`,
        );
      }
      setLiveMessages((prev) => [
        ...prev.filter(
          (m) =>
            !(
              m.kind === "tool_permission" &&
              m.toolPermission?.status === "pending"
            ),
        ),
        {
          id: nextId(),
          role: "user",
          kind: "interrupt",
          text: "",
          timestamp: new Date(),
        },
      ]);
      streamResolveRef.current?.();
    } catch (e) {
      alert("error", `Couldn't stop: ${(e as Error).message}`);
    } finally {
      setIsStopping(false);
    }
  }, []);

  const sendMessage = useCallback(
    async (text: string) => {
      // Read from ref (sync-current) so a clearSession that ran in the
      // same handler is honored — state would still hold the old array.
      const turnAttachments = pendingAttachmentsRef.current;
      setPendingAttachments([]);
      await performSend(text, turnAttachments);
    },
    [performSend, setPendingAttachments],
  );

  const retry = useCallback(async () => {
    if (isBackgrounded) return;

    const lastMsg = lastUserMessageRef.current;
    const lastAttachments = lastTurnAttachmentsRef.current;
    if (!lastMsg && lastAttachments.length === 0) return;

    setLiveMessages((prev) => {
      let lastUserIdx = -1;
      for (let i = prev.length - 1; i >= 0; i--) {
        if (prev[i].role === "user") {
          lastUserIdx = i;
          break;
        }
      }
      if (lastUserIdx === -1) return prev;
      return prev.slice(0, lastUserIdx);
    });
    setError(null);

    await performSend(lastMsg ?? "", lastAttachments);
  }, [performSend, isBackgrounded]);

  const clearSession = useCallback(() => {
    activeSessionRef.current = null;
    sessionPromiseRef.current = null;
    // Reset turn refs first so performSend's catch/finally see null
    // placeholderIdRef and skip DOM cleanup on the now-empty list.
    resetTurnRefs();
    // Settle an in-flight stream promise so awaiting performSend
    // unwinds cleanly instead of hanging until idle-timeout.
    streamResolveRef.current?.();
    streamResolveRef.current = null;
    streamRejectRef.current = null;
    clearIdleTimer();
    turnStartedAtRef.current = null;
    setIsStreaming(false);
    setAgentSessionUuid(null);
    setHydratedEvents([]);
    setHydratedPendingPermissions([]);
    setLiveMessages([]);
    setError(null);
    setIsResumedSession(false);
    setIsBackgrounded(false);
    setIsHydrating(false);
    setHydrationError(null);
    setNextHistoryCursor(null);
    setIsLoadingMoreHistory(false);
    setEffort(defaultEffort);
    setPendingAttachments([]);
    setSessionAttachments([]);
    setEscalationRequest(null);
    lastUserMessageRef.current = null;
    lastTurnAttachmentsRef.current = [];
    msgIdRef.current = 0;
    suppressedHydratedRequestIdsRef.current = new Set();
    setClientResolvedToolUseIds(new Set());
    setSeededTrailingToolUseIds(new Set());
  }, [
    clearIdleTimer,
    defaultEffort,
    resetTurnRefs,
    setPendingAttachments,
  ]);

  // Provider sits above the route tree, so without this its state
  // would leak across logout into the next user's session.
  useEffect(() => {
    if (!isUserAuthenticated) {
      clearSession();
    }
  }, [isUserAuthenticated, clearSession]);

  const stageAttachment = useCallback(
    async (file: File): Promise<AttachmentStageError | null> => {
      // Count in-flight uploads too — parallel callers see a stale
      // pendingAttachments snapshot from closure.
      const projected =
        pendingAttachments.length + stagingInFlightRef.current;
      if (projected >= MAX_ATTACHMENTS_PER_MESSAGE) {
        return {
          kind: "cap-reached",
          max: MAX_ATTACHMENTS_PER_MESSAGE,
        };
      }
      const ext = extensionForFile(file);
      if (!ext) return { kind: "unsupported-type" };
      if (file.size > MAX_ATTACHMENT_SIZE_MB * 1024 * 1024) {
        return { kind: "too-large", maxMB: MAX_ATTACHMENT_SIZE_MB };
      }

      stagingInFlightRef.current += 1;
      setIsStagingAttachment(true);
      try {
        const uuid = await ensureSession();
        const sessionAtStart = uuid;

        const signResponse = await signS3AgentSessionAttachment(
          ext,
          uuid,
        );
        const { data: signData, storageObjectName } =
          signResponse.data;
        const { url, fields } = signData;

        const formData = new FormData();
        for (const [k, v] of Object.entries(fields)) {
          formData.append(k, v as string);
        }
        formData.append("file", file);
        formData.append("Content-Type", file.type);
        await axios.post(url, formData);

        const { data: mutationData } =
          await uploadAgentSessionAttachment({
            variables: {
              storageObjectName,
              agentSessionUuid: uuid,
              fileName: file.name,
              contentType: file.type,
            },
          });
        const registered = mutationData?.uploadAgentSessionAttachment;
        if (!registered?.ok || !registered.uuid) {
          throw new Error("Server rejected attachment.");
        }

        const uploadedUuid = registered.uuid;
        setPendingAttachments((prev) => {
          if (activeSessionRef.current !== sessionAtStart)
            return prev;
          return [
            ...prev,
            {
              uuid: uploadedUuid,
              fileName: file.name,
              contentType: file.type,
            },
          ];
        });
        return null;
      } catch (e) {
        return {
          kind: "upload-failed",
          message: (e as Error).message || "Upload failed",
        };
      } finally {
        stagingInFlightRef.current = Math.max(
          0,
          stagingInFlightRef.current - 1,
        );
        if (stagingInFlightRef.current === 0) {
          setIsStagingAttachment(false);
        }
      }
    },
    [
      pendingAttachments,
      ensureSession,
      uploadAgentSessionAttachment,
      setPendingAttachments,
    ],
  );

  const removePendingAttachment = useCallback(
    (uuid: string) => {
      setPendingAttachments((prev) =>
        prev.filter((a) => a.uuid !== uuid),
      );
    },
    [setPendingAttachments],
  );

  const fetchHistoryPage = useCallback(
    async (agentSessionUuidArg: string, after: string | null) => {
      const { data } = await fetchAgentSessionEvents({
        variables: { agentSessionUuid: agentSessionUuidArg, after },
      });
      const page = data?.agentSessionEvents;
      return {
        events: page?.events ?? [],
        nextPage: page?.nextPage ?? null,
        pendingPermissions: page?.pendingPermissions ?? [],
        turnInFlight: page?.turnInFlight ?? false,
      };
    },
    [fetchAgentSessionEvents],
  );

  const hydrateSession = useCallback(
    async (agentSessionUuidArg: string) => {
      setIsHydrating(true);
      setHydrationError(null);
      try {
        const [history, attachments] = await Promise.all([
          fetchHistoryPage(agentSessionUuidArg, null),
          fetchAgentSessionAttachments({
            variables: { agentSessionUuid: agentSessionUuidArg },
          }),
        ]);
        if (activeSessionRef.current !== agentSessionUuidArg) return;
        setHydratedEvents(history.events);
        setNextHistoryCursor(history.nextPage);
        setHydratedPendingPermissions(
          history.pendingPermissions.map(
            permissionPayloadFromHydrated,
          ),
        );

        const { toolCalls: seededTools, personas: seededPersonas } =
          history.turnInFlight
            ? extractTrailingToolUses(history.events)
            : { toolCalls: [], personas: [] };
        setSeededTrailingToolUseIds(
          new Set(seededTools.map((t) => t.id)),
        );

        if (history.turnInFlight && !turnInFlightRef.current) {
          const { streamPromise, disarm } = armTurn({
            toolCalls: seededTools.length ? seededTools : undefined,
            personas: seededPersonas.length
              ? seededPersonas
              : undefined,
          });

          streamPromise
            .catch((err) => {
              if (activeSessionRef.current !== agentSessionUuidArg)
                return;
              setError({
                content:
                  (err as Error).message ||
                  "Something went wrong. Please try again.",
                canRetry: true,
              });
            })
            .finally(disarm);
        }

        const rows = attachments.data?.agentSessionAttachments ?? [];
        setSessionAttachments(
          rows
            .filter(
              (r): r is NonNullable<typeof r> =>
                !!r && typeof r.uuid === "string",
            )
            .map<CompanionV2Attachment>((r) => ({
              uuid: r.uuid as string,
              fileName: r.fileName ?? "attachment",
              contentType:
                r.contentType ?? "application/octet-stream",
              fileLink: sanitizeFileLink(r.fileLink),
              source: r.managedFileId ? "agent" : "user",
              eventId: r.sentInEventId ?? null,
            })),
        );
      } catch (e) {
        if (activeSessionRef.current !== agentSessionUuidArg) return;
        setHydrationError(
          (e as Error).message || "Failed to load history",
        );
      } finally {
        if (activeSessionRef.current === agentSessionUuidArg) {
          setIsHydrating(false);
        }
      }
    },
    [fetchHistoryPage, fetchAgentSessionAttachments, armTurn],
  );

  const loadSession = useCallback(
    (uuid: string) => {
      activeSessionRef.current = uuid;
      sessionPromiseRef.current = null;
      resetTurnRefs();
      streamResolveRef.current?.();
      streamResolveRef.current = null;
      streamRejectRef.current = null;
      clearIdleTimer();
      turnStartedAtRef.current = null;
      setIsStreaming(false);
      setAgentSessionUuid(uuid);
      setHydratedEvents([]);
      setLiveMessages([]);
      setError(null);
      setIsBackgrounded(false);
      setHydrationError(null);
      setIsResumedSession(true);
      setNextHistoryCursor(null);
      setIsLoadingMoreHistory(false);
      setPendingAttachments([]);
      setSessionAttachments([]);
      setEscalationRequest(null);
      lastUserMessageRef.current = null;
      lastTurnAttachmentsRef.current = [];
      msgIdRef.current = 0;
      suppressedHydratedRequestIdsRef.current = new Set();
      setClientResolvedToolUseIds(new Set());
      setSeededTrailingToolUseIds(new Set());
      hydrateSession(uuid);
    },
    [
      hydrateSession,
      clearIdleTimer,
      resetTurnRefs,
      setPendingAttachments,
    ],
  );

  const retryHydration = useCallback(async () => {
    if (!agentSessionUuid) return;
    await hydrateSession(agentSessionUuid);
  }, [agentSessionUuid, hydrateSession]);

  const acceptNavProposal = useCallback(
    (messageId: string) => {
      setLiveMessages((prev) => {
        const card = prev.find((m) => m.id === messageId);
        if (card?.navProposal) {
          mixpanel.track("Companion Nav Proposal Accepted", {
            entityType: card.navProposal.kind,
            entityUuid: card.navProposal.entity_uuid,
            page: card.navProposal.page,
            tab: card.navProposal.tab,
            path: card.navProposal.path,
            label: card.navProposal.label,
            reason: card.navProposal.reason,
            agentSessionUuid: activeSessionRef.current,
          });
          navigate(card.navProposal.path);
        }
        return prev.filter((m) => m.id !== messageId);
      });
    },
    [navigate],
  );

  const dismissNavProposal = useCallback((messageId: string) => {
    setLiveMessages((prev) => {
      const card = prev.find((m) => m.id === messageId);
      if (card?.navProposal) {
        mixpanel.track("Companion Nav Proposal Dismissed", {
          entityType: card.navProposal.kind,
          entityUuid: card.navProposal.entity_uuid,
          page: card.navProposal.page,
          tab: card.navProposal.tab,
          path: card.navProposal.path,
          label: card.navProposal.label,
          reason: card.navProposal.reason,
          agentSessionUuid: activeSessionRef.current,
        });
      }
      return prev.filter((m) => m.id !== messageId);
    });
  }, []);

  const submitToolPermissionDecision = useCallback(
    async (
      messageId: string,
      permission: ToolPermissionPayload,
      decision: ToolPermissionDecision,
      remember = false,
    ) => {
      if (permission.status !== "pending") return;
      if (resolvingPermissionIdsRef.current.has(permission.requestId))
        return;
      resolvingPermissionIdsRef.current.add(permission.requestId);

      const matchById = (m: CompanionV2Message) => m.id === messageId;
      updatePermissionMessage(matchById, (p) => ({
        ...p,
        isResolving: true,
        error: null,
      }));

      // Arm own turn before the fetch so racing WS events aren't gated out.
      const armedHere = !turnInFlightRef.current;
      let streamPromise: Promise<void> | null = null;
      let disarm: (() => void) | null = null;
      // Object box so error/expired/404 paths only close the turn we armed.
      const ownTurn: { resolve: (() => void) | null } = {
        resolve: null,
      };
      if (armedHere) {
        setError(null);
        const armed = armTurn();
        streamPromise = armed.streamPromise;
        ownTurn.resolve = armed.resolve;
        disarm = armed.disarm;
      }

      try {
        const res = await fetch(
          `${AGENT_API}/tool-permissions/${permission.requestId}/decision`,
          {
            method: "POST",
            credentials: "include",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ decision, remember }),
          },
        );
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          if (res.status === 404) {
            // Server-side expired: drop card, suppress re-promotion.
            suppressedHydratedRequestIdsRef.current.add(
              permission.requestId,
            );
            setLiveMessages((prev) =>
              prev.filter((m) => m.id !== messageId),
            );
            ownTurn.resolve?.();
            return;
          }
          throw new Error(
            body.detail || `Request failed (HTTP ${res.status})`,
          );
        }
        const data: { status?: ToolPermissionStatus } =
          await res.json();
        const nextStatus: ToolPermissionStatus =
          data.status === "approved" ||
          data.status === "rejected" ||
          data.status === "expired"
            ? data.status
            : decision === "approve"
              ? "approved"
              : "rejected";
        updatePermissionMessage(matchById, (p) => ({
          ...p,
          status: nextStatus,
          isResolving: false,
          error: null,
        }));
        setClientResolvedToolUseIds((prev) => {
          if (prev.has(permission.requestId)) return prev;
          const next = new Set(prev);
          next.add(permission.requestId);
          return next;
        });

        // Expired = no agent activity follows; don't await turn-end.
        if (nextStatus === "expired") {
          ownTurn.resolve?.();
        } else if (streamPromise) {
          try {
            await streamPromise;
          } catch (err) {
            setError({
              content:
                (err as Error).message ||
                "Something went wrong. Please try again.",
              canRetry: true,
            });
          }
        }
      } catch (e) {
        updatePermissionMessage(matchById, (p) => ({
          ...p,
          isResolving: false,
          error:
            (e as Error).message || "Could not send your decision.",
        }));
        ownTurn.resolve?.();
      } finally {
        resolvingPermissionIdsRef.current.delete(
          permission.requestId,
        );
        disarm?.();
      }
    },
    [updatePermissionMessage, armTurn],
  );

  useEffect(() => {
    const turnRoute = turnRouteRef.current;
    if (!turnRoute || routeKey === turnRoute) return;

    for (const message of liveMessages) {
      const permission = message.toolPermission;
      if (
        !permission ||
        permission.status !== "pending" ||
        permission.isResolving ||
        permission.error
      )
        continue;
      void submitToolPermissionDecision(
        message.id,
        permission,
        "reject",
      );
    }
  }, [routeKey, liveMessages, submitToolPermissionDecision]);

  const decideToolPermission = useCallback(
    async (
      messageId: string,
      decision: ToolPermissionDecision,
      remember = false,
    ) => {
      const card = messages.find((m) => m.id === messageId);
      const permission = card?.toolPermission;
      if (!permission || permission.status !== "pending") return;
      await submitToolPermissionDecision(
        messageId,
        permission,
        decision,
        remember,
      );
    },
    [messages, submitToolPermissionDecision],
  );

  const loadMoreHistory = useCallback(async () => {
    const target = activeSessionRef.current;
    if (!target || !nextHistoryCursor || isLoadingMoreHistory) return;
    setIsLoadingMoreHistory(true);
    setHydrationError(null);
    try {
      const { events, nextPage } = await fetchHistoryPage(
        target,
        nextHistoryCursor,
      );
      if (activeSessionRef.current !== target) return;
      setHydratedEvents((prev) => [...events, ...prev]);
      setNextHistoryCursor(nextPage);
    } catch (e) {
      if (activeSessionRef.current !== target) return;
      setHydrationError(
        (e as Error).message || "Failed to load earlier messages",
      );
    } finally {
      setIsLoadingMoreHistory(false);
    }
  }, [nextHistoryCursor, isLoadingMoreHistory, fetchHistoryPage]);

  const [tooltip, setTooltip] = useState<CompanionTooltip | null>(
    null,
  );

  const dismissCompanionTooltip = useCallback(() => {
    setTooltip(null);
  }, []);

  const showCompanionTooltip = useCallback(
    (input: ShowCompanionTooltipInput) => {
      if (companionTooltipsDisabled || isAnyTourActive()) return;
      const tooltipId =
        input.id ??
        `tt-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      const tracking = input.tracking;
      const trackingProps = tracking
        ? {
            trigger: tracking.trigger,
            ...(tracking.properties ?? {}),
          }
        : null;

      if (trackingProps) {
        mixpanel.track("Companion Tooltip Shown", trackingProps);
      }

      const wrappedActions = (input.actions ?? []).map((action) => ({
        ...action,
        onClick: () => {
          if (trackingProps) {
            mixpanel.track("Companion Tooltip Clicked", {
              ...trackingProps,
              action: action.key ?? null,
            });
          }
          action.onClick?.();
        },
      }));

      const wrappedDismiss = () => {
        if (trackingProps) {
          mixpanel.track(
            "Companion Tooltip Dismissed",
            trackingProps,
          );
        }
        input.onDismiss?.();
      };

      setTooltip({
        id: tooltipId,
        companions: input.companions,
        content: input.content,
        actions: wrappedActions,
        onDismiss: wrappedDismiss,
      });
    },
    [companionTooltipsDisabled],
  );

  useEffect(() => {
    if (companionTooltipsDisabled) setTooltip(null);
  }, [companionTooltipsDisabled]);

  return {
    messages,
    isStreaming,
    isCreatingSession,
    agentSessionUuid,
    isResumedSession,
    error,
    isHydrating,
    hydrationError,
    hasMoreHistory: nextHistoryCursor !== null,
    hasPendingToolPermission,
    isLoadingMoreHistory,
    effort,
    setEffort,
    pendingAttachments,
    isStagingAttachment,
    isBackgrounded,
    isStopping,
    sendMessage,
    stopGeneration,
    retry,
    clearError,
    clearSession,
    loadSession,
    loadMoreHistory,
    retryHydration,
    acceptNavProposal,
    dismissNavProposal,
    decideToolPermission,
    stageAttachment,
    removePendingAttachment,
    escalationRequest,
    clearEscalationRequest,
    tooltip,
    showCompanionTooltip,
    dismissCompanionTooltip,
  };
}
